// domains/Grid/hooks/usePostGrid.ts - Updated to use planning API
import { useCallback } from "react";
import { useAppStore } from "@/core/store/useAppStore";
import { GridItem, isEmptyGridSlot } from "../types/grid.types";
import { Post } from "@/core/types/postTypes";
import { planningApi } from "../api/planningApi";

export interface UsePostGridReturn {
  items: GridItem[];
  selectedItemId: string | null;
  isLoading: boolean;
  error: string | null;
  currentGridMode: "live" | "experimental" | "ai-suggest";
  activeGridId: string | number | null;

  // Grid management
  updateItem: (position: number, updates: Partial<GridItem>) => void;
  selectItem: (id: string | null) => void;
  clearGrid: () => void;
  loadGrid: (items: GridItem[]) => void;
  setGridMode: (mode: "live" | "experimental" | "ai-suggest") => void;

  // Post operations
  setPost: (position: number, post: Post | null) => void;
  removePost: (position: number) => void;
  swapItems: (sourcePosition: number, targetPosition: number) => void;

  // Grid loading
  loadLiveGrid: () => Promise<void>;
  loadExperimentalGrids: () => Promise<void>;
  generateAIGrid: (preferences?: any) => Promise<void>;

  // Grid actions
  addPostToGrid: (postId: string, position: number) => Promise<void>;
  removePostFromGrid: (postId: string) => Promise<void>;
}

export const usePostGrid = (): UsePostGridReturn => {
  // ✅ Fixed: Destructure directly from store (flattened properties)
  const {
    selectedClient,
    // Grid state properties (flattened)
    items,
    selectedItemId,
    isLoading,
    error,
    currentGridMode,
    activeGridId,
    // Grid action methods
    updateGridItem,
    setSelectedItem,
    clearGrid,
    loadGrid,
    setGridMode,
    setGridLoading,
    setGridError,
    swapGridPositions,
  } = useAppStore();

  const setPost = useCallback(
    (position: number, post: Post | null) => {
      if (post === null) {
        // Reset to empty placeholder when removing post
        updateGridItem(position, {
          id: `empty-${position}`,
          post: null,
          isEmpty: true,
          updatedAt: new Date().toISOString(),
        });
        return;
      }

      const item = items.find((i) => i.position === position);
      const isCurrentlyEmpty = isEmptyGridSlot(item!);

      const updates: Partial<GridItem> = {
        post,
        isEmpty: false,
        updatedAt: new Date().toISOString(),
      };

      // Generate new ID for empty slots
      if (isCurrentlyEmpty) {
        updates.id = `grid-item-${post.id}-${position}`;
        updates.createdAt = new Date().toISOString();
      }

      updateGridItem(position, updates);
    },
    [updateGridItem, items],
  );

  const removePost = useCallback(
    (position: number) => {
      updateGridItem(position, {
        id: `empty-${position}`,
        post: null,
        isEmpty: true,
        updatedAt: new Date().toISOString(),
      });
    },
    [updateGridItem],
  );

  const swapItems = useCallback(
    (sourcePosition: number, targetPosition: number) => {
      swapGridPositions(sourcePosition, targetPosition);
    },
    [swapGridPositions],
  );

  const loadLiveGrid = useCallback(async () => {
    if (!selectedClient?.id) return;

    setGridLoading(true);
    setGridError(null);

    try {
      // Get all grids and find the active one
      const grids = await planningApi.listGrids(selectedClient.id);
      const activeGrid = grids.find(g => g.isActive);

      if (!activeGrid) {
        // No active grid - show empty
        loadGrid([]);
        setGridMode("live");
        return;
      }

      // Transform backend grid items to frontend format
      const gridItems: GridItem[] = activeGrid.gridItems.map(item => ({
        id: item.id,
        position: item.position,
        post: item.post,
        isEmpty: !item.post,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      }));

      loadGrid(gridItems);
      setGridMode("live");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load active grid";
      setGridError(errorMessage);
      console.error("Error loading active grid:", err);
    } finally {
      setGridLoading(false);
    }
  }, [selectedClient?.id, setGridLoading, setGridError, loadGrid, setGridMode]);

  const loadExperimentalGrids = useCallback(async () => {
    if (!selectedClient?.id) return;

    setGridLoading(true);
    setGridError(null);

    try {
      // Get all grids and find draft grids
      const grids = await planningApi.listGrids(selectedClient.id);
      const draftGrids = grids.filter(g => g.status === "draft" && !g.isActive);

      // For now, load the first draft grid or create empty
      if (draftGrids.length > 0) {
        const firstGrid = draftGrids[0];

        // Transform backend grid items to frontend format
        const gridItems: GridItem[] = firstGrid.gridItems.map(item => ({
          id: item.id,
          position: item.position,
          post: item.post,
          isEmpty: !item.post,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        }));

        loadGrid(gridItems);
      } else {
        clearGrid();
      }

      setGridMode("experimental");
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to load draft grids";
      setGridError(errorMessage);
      console.error("Error loading draft grids:", err);
    } finally {
      setGridLoading(false);
    }
  }, [
    selectedClient?.id,
    setGridLoading,
    setGridError,
    loadGrid,
    clearGrid,
    setGridMode,
  ]);

  const generateAIGrid = useCallback(
    async (preferences?: any) => {
      if (!selectedClient?.id) return;

      setGridLoading(true);
      setGridError(null);

      try {
        // AI grid generation not yet implemented in new planning API
        // For now, just show a message
        setGridError("AI grid generation coming soon");
        clearGrid();
        setGridMode("ai-suggest");
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate AI grid";
        setGridError(errorMessage);
        console.error("Error generating AI grid:", err);
      } finally {
        setGridLoading(false);
      }
    },
    [selectedClient?.id, setGridLoading, setGridError, clearGrid, setGridMode],
  );

  const addPostToGrid = useCallback(
    async (postId: string, position: number) => {
      if (!selectedClient?.id || !activeGridId) return;

      try {
        // Only add to experimental grids (active grids are read-only)
        if (
          currentGridMode === "experimental" &&
          typeof activeGridId === "number"
        ) {
          await planningApi.addPostToGrid(
            activeGridId,
            postId,
            position,
            selectedClient.id,
          );

          // Reload the current grid to reflect changes
          await loadExperimentalGrids();
        } else {
          console.warn("Can only add posts to draft grids");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to add post to grid";
        setGridError(errorMessage);
        console.error("Error adding post to grid:", err);
      }
    },
    [
      selectedClient?.id,
      activeGridId,
      currentGridMode,
      loadExperimentalGrids,
      setGridError,
    ],
  );

  const removePostFromGrid = useCallback(
    async (postId: string) => {
      if (!selectedClient?.id || !activeGridId) return;

      try {
        // Only remove from experimental grids (active grids are read-only)
        if (
          currentGridMode === "experimental" &&
          typeof activeGridId === "number"
        ) {
          await planningApi.removePostFromGrid(
            activeGridId,
            postId,
            selectedClient.id,
          );

          // Reload the current grid to reflect changes
          await loadExperimentalGrids();
        } else {
          console.warn("Can only remove posts from draft grids");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to remove post from grid";
        setGridError(errorMessage);
        console.error("Error removing post from grid:", err);
      }
    },
    [
      selectedClient?.id,
      activeGridId,
      currentGridMode,
      loadExperimentalGrids,
      setGridError,
    ],
  );

  return {
    items,
    selectedItemId,
    isLoading,
    error,
    currentGridMode,
    activeGridId,
    updateItem: updateGridItem,
    selectItem: setSelectedItem,
    clearGrid,
    loadGrid,
    setGridMode,
    setPost,
    removePost,
    swapItems,
    loadLiveGrid,
    loadExperimentalGrids,
    generateAIGrid,
    addPostToGrid,
    removePostFromGrid,
  };
};
